import axios from 'axios';
import api from '../utils/api';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5002';

const API_ENDPOINT = '/kantasks';  // Changed to endpoint path only since baseURL is in api.js

// Fallback direct axios call for PATCH requests if the API utility fails
const directPatchRequest = async (url, data) => {
  const token = localStorage.getItem('token');
  try {
    console.log('Attempting direct PATCH request to:', url);
    const response = await axios({
      method: 'PATCH',
      url: `${API_BASE_URL}/api/v1${url}`,
      data,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'x-auth-token': token
      },
      withCredentials: true
    });
    return response.data;
  } catch (error) {
    console.error('Direct PATCH request failed:', error);
    throw error;
  }
};

// POST request that acts as a PATCH (workaround for CORS issues)
const postAsPatchRequest = async (taskId, data) => {
  try {
    console.log('Using POST as PATCH workaround for task:', taskId);
    const response = await api.post(`${API_ENDPOINT}/${taskId}/update`, data);
    console.log('Task updated successfully with POST workaround:', response.data);
    return response.data;
  } catch (error) {
    console.error('POST as PATCH workaround failed:', error);
    throw error;
  }
};

export const getProjectTasks = async (projectId) => {
  try {
    console.log('Fetching tasks for project:', projectId);
    const response = await api.get(`${API_ENDPOINT}/project/${projectId}`);
    console.log('Fetched tasks from server:', response.data);
    
    // Process the tasks to ensure crew members are properly formatted
    const processedTasks = {};
    
    Object.keys(response.data).forEach(status => {
      processedTasks[status] = response.data[status].map(task => {
        // Ensure crew members are properly formatted
        const processedCrewMembers = (task.crewMembers || []).map(member => {
          // If it's already a complete object, use it
          if (member && typeof member === 'object') {
            return member;
          }
          // If it's just an ID string, create a minimal object
          if (typeof member === 'string') {
            return { _id: member };
          }
          return member;
        }).filter(Boolean); // Remove any null values
        
        return {
          ...task,
          crewMembers: processedCrewMembers
        };
      });
    });
    
    console.log('Processed tasks with crew members:', processedTasks);
    return processedTasks;
  } catch (error) {
    console.error('Failed to fetch tasks:', error.response?.data || error.message);
    throw new Error(error.response?.data?.message || 'Failed to fetch tasks');
  }
};

export const createTask = async (taskData) => {
  try {
    console.log('Creating task with data:', taskData);
    if (!taskData.projectId) {
      throw new Error('Project ID is required');
    }
    const response = await api.post(API_ENDPOINT, taskData);
    console.log('Task created successfully:', response.data);
    return response.data;
  } catch (error) {
    console.error('Failed to create task:', error.response?.data || error.message);
    throw new Error(error.response?.data?.message || 'Failed to create task');
  }
};

export const updateTask = async (taskId, taskData) => {
  try {
    console.log('Updating task:', taskId, 'with data:', taskData);
    
    // Use POST as PATCH workaround (most reliable method)
    try {
      return await postAsPatchRequest(taskId, taskData);
    } catch (postError) {
      console.warn('POST as PATCH workaround failed, trying API utility:', postError);
      
      // If that fails, try with the API utility
      try {
        const response = await api.patch(`${API_ENDPOINT}/${taskId}`, taskData);
        console.log('Task updated successfully with API utility:', response.data);
        return response.data;
      } catch (apiError) {
        console.warn('API utility failed for PATCH, trying direct axios call:', apiError);
        
        // If that fails, try direct axios call
        const directResponse = await directPatchRequest(`${API_ENDPOINT}/${taskId}`, taskData);
        console.log('Task updated successfully with direct call:', directResponse);
        return directResponse;
      }
    }
  } catch (error) {
    console.error('Failed to update task:', error.response?.data || error.message);
    throw new Error(error.response?.data?.message || 'Failed to update task');
  }
};

export const deleteTask = async (taskId) => {
  try {
    console.log('Deleting task:', taskId);
    const response = await api.delete(`${API_ENDPOINT}/${taskId}`);
    console.log('Task deleted successfully');
    return response.data;
  } catch (error) {
    console.error('Failed to delete task:', error.response?.data || error.message);
    throw new Error(error.response?.data?.message || 'Failed to delete task');
  }
};

export const addComment = async (taskId, content, author) => {
  try {
    console.log('Adding comment to task:', taskId);
    const response = await api.post(
      `${API_ENDPOINT}/${taskId}/comments`,
      { content, author }
    );
    console.log('Comment added successfully:', response.data);
    return response.data;
  } catch (error) {
    console.error('Failed to add comment:', error.response?.data || error.message);
    throw new Error(error.response?.data?.message || 'Failed to add comment');
  }
};

export const addMessage = async (taskId, content, senderId, mentions = []) => {
  try {
    console.log('Adding message to task:', taskId);
    const response = await api.post(
      `${API_ENDPOINT}/${taskId}/messages`,
      { 
        content, 
        sender: senderId,
        mentions 
      }
    );
    console.log('Message added successfully:', response.data);
    return response.data;
  } catch (error) {
    console.error('Failed to add message:', error.response?.data || error.message);
    throw new Error(error.response?.data?.message || 'Failed to add message');
  }
};

export const getTaskMessages = async (taskId) => {
  try {
    console.log('Fetching messages for task:', taskId);
    const response = await api.get(`${API_ENDPOINT}/${taskId}/messages`);
    console.log('Fetched messages successfully:', response.data);
    return response.data;
  } catch (error) {
    console.error('Failed to fetch messages:', error.response?.data || error.message);
    throw new Error(error.response?.data?.message || 'Failed to fetch messages');
  }
};

export const updateProgress = async (taskId, progress) => {
  try {
    console.log('Updating task progress:', taskId, progress);
    
    try {
      // First try with the API utility
      const response = await api.patch(
        `${API_ENDPOINT}/${taskId}/progress`,
        { progress }
      );
      console.log('Progress updated successfully:', response.data);
      return response.data;
    } catch (apiError) {
      console.warn('API utility failed for PATCH, trying direct axios call:', apiError);
      // If that fails, try direct axios call
      const directResponse = await directPatchRequest(`${API_ENDPOINT}/${taskId}/progress`, { progress });
      console.log('Progress updated successfully with direct call:', directResponse);
      return directResponse;
    }
  } catch (error) {
    console.error('Failed to update progress:', error.response?.data || error.message);
    throw new Error(error.response?.data?.message || 'Failed to update progress');
  }
};

export const updateWatcher = async (taskId, userId, action) => {
  try {
    console.log('Updating task watchers:', taskId, userId, action);
    
    try {
      // First try with the API utility
      const response = await api.patch(
        `${API_ENDPOINT}/${taskId}/watchers`,
        { userId, action }
      );
      console.log('Watchers updated successfully:', response.data);
      return response.data;
    } catch (apiError) {
      console.warn('API utility failed for PATCH, trying direct axios call:', apiError);
      // If that fails, try direct axios call
      const directResponse = await directPatchRequest(`${API_ENDPOINT}/${taskId}/watchers`, { userId, action });
      console.log('Watchers updated successfully with direct call:', directResponse);
      return directResponse;
    }
  } catch (error) {
    console.error('Failed to update watchers:', error.response?.data || error.message);
    throw new Error(error.response?.data?.message || 'Failed to update watchers');
  }
};
